import React, { useEffect, useMemo, useState } from 'react'
import { Image } from 'react-bootstrap'
import { openLinkInNewTab, openInSameTab, fromNow, minutesSince } from '../Utils/Helpers'
import { useSelector, useDispatch } from 'react-redux'
import { IoShareOutline } from 'react-icons/io5'
import { BsDot } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'
import GoogleAds from './GoogleAds'
import Coverage from './Coverage'
import config from '../Config'
import { setShareLink } from '../Stores/Actions'
import { IS_MOBILE } from '../Contstants'
import { logEvent } from '@firebase/analytics'
import analytics from '../Utils/FirebaseAnalytics'

const SOURCES_WITH_BLOCKED_IFRAME = [
  'Hayat.ba',
  'Radio Sarajevo',
  'Mondo',
  'Buka',
  'Bljesak.info',
  'Al Jazeera Balkans',
]

export default function NewsCard(props) {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  /*
    news: News item that is displayed in the card
    variant: 'hero' renders the lead story (bigger headline, other portals' headlines; no summary)
    includeAd: Show ad for specific news indexes
  */
  const { news, variant, includeAd, isDescriptionVisible, category, isSimilarNews } = props
  const isHero = variant === 'hero'

  const [shareCount, setShareCount] = useState(news?.share_count ?? 0)

  // Only the article's own small thumbnail (copyright: no large images, no
  // images borrowed from another portal's article) and never the portal logo,
  // which made cards look like ads. A card without a thumbnail is text-only.
  const [imageFailed, setImageFailed] = useState(false)
  useEffect(() => setImageFailed(false), [news?.image]) // lists key by index, cards get reused
  const image = imageFailed ? null : news?.image

  const portalsList = useSelector((state) => state.portals.list)
  const shareData = useSelector((state) => state.share)

  const logoOf = (portalName) => portalsList?.find((p) => p.title === portalName)?.logo
  const portalImage = logoOf(news.portal)

  const date = fromNow(news?.pub_date)
  const portals = news?.portals ?? [news?.portal]
  const isGrouped = Boolean(news?.group_id) && !isSimilarNews

  // "U razvoju": a multi-portal story still gaining articles; "Novo": just published.
  const badge = useMemo(() => {
    if (isSimilarNews) return null
    if ((news?.new_last_hour ?? 0) >= 3 && portals.length >= 2) {
      return { className: 'badge-developing', label: `U razvoju · +${news.new_last_hour}` }
    }
    if (minutesSince(news?.pub_date) < 15) {
      return { className: 'badge-new', label: 'Novo' }
    }
    return null
  }, [news?.new_last_hour, news?.pub_date, portals.length, isSimilarNews])

  const openArticle = (newsSource, newsLink) => {
    if (IS_MOBILE) {
      SOURCES_WITH_BLOCKED_IFRAME.includes(newsSource)
        ? openInSameTab(newsLink)
        : navigate(`/m-news?link=${newsLink}`)
    } else {
      openLinkInNewTab(newsLink)
    }
  }

  const handleCardClick = () => {
    logEvent(analytics, 'w_news_opened')
    openArticle(news.portal, news.link)
  }

  const onSimilarNewsPress = (event) => {
    event.stopPropagation()
    navigate(`/povezano/${news.id}`)
    logEvent(analytics, 'w_similar_news_opened')
  }

  const shareNews = () => {
    const data = {
      visibility: true,
      shareUrl: `${config.SHARE_URL}${news.id}`,
      title: news.title,
      newsId: news.id,
    }
    dispatch(setShareLink(data))
    logEvent(analytics, 'w_news_shared')
  }

  // Update share count if news has been shared
  useEffect(() => {
    if (shareData?.newsId == news?.id && !shareData?.visibility) {
      setShareCount(shareCount + 1)

      const data = { ...shareData, newsId: null }
      dispatch(setShareLink(data))
    }
  }, [shareData])

  const paragraph = news?.description
  const showDescription = isDescriptionVisible && paragraph

  const imageElement = image ? (
    <img
      onError={() => setImageFailed(true)}
      className="card-image"
      referrerPolicy="no-referrer"
      alt=""
      loading={isHero ? 'eager' : 'lazy'}
      src={image}
    />
  ) : null

  return (
    <>
      {includeAd && <GoogleAds />}
      <article className={`card ${isHero ? 'card-hero' : ''}`} onClick={() => handleCardClick()}>
        <div className="cont-cont">
          <div className="card-body-cont">
            {isHero && <div className="card-hero-label">{`Glavna vijest`}</div>}
            <div className="card-meta">
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(
                    IS_MOBILE && category
                      ? `/portal/${news.portal}?cat=${category}`
                      : `/portal/${news.portal}`
                  )
                }}
                className="card-source"
              >
                {portalImage && <Image roundedCircle src={portalImage} />}
                <span className="portal-name">{news?.portal}</span>
                <BsDot />
                <span className="card-time">{date}</span>
              </div>
              {badge && <span className={`card-badge ${badge.className}`}>{badge.label}</span>}
            </div>

            <h2 className="card-title">{news?.title}</h2>
            {showDescription ? <p className="card-description">{paragraph}</p> : null}
          </div>
          {imageElement}
        </div>

        {isHero && isGrouped && news?.coverage?.length > 0 && (
          <div className="card-coverage-list">
            <div className="card-coverage-title">{`Kako su pisali drugi`}</div>
            {news.coverage.map((item) => (
              <div
                key={item.id}
                className="card-coverage-item"
                onClick={(e) => {
                  e.stopPropagation()
                  logEvent(analytics, 'w_news_opened')
                  openArticle(item.portal, item.link)
                }}
              >
                {logoOf(item.portal) && (
                  <img src={logoOf(item.portal)} alt="" referrerPolicy="no-referrer" />
                )}
                <span className="card-coverage-portal">{item.portal}</span>
                <span className="card-coverage-headline">{item.title}</span>
              </div>
            ))}
          </div>
        )}

        <div className="card-footer-row">
          {isGrouped ? (
            <Coverage
              portals={portals}
              articles={(news?.similar_news_count ?? 0) + 1}
              onClick={(e) => onSimilarNewsPress(e)}
            />
          ) : (
            <span />
          )}
          <button
            type="button"
            aria-label="Podijeli"
            onClick={(e) => {
              e.stopPropagation()
              shareNews()
            }}
            className="share-news-div"
          >
            <IoShareOutline className="share-news-icon" />
            {shareCount > 0 && <span>{shareCount}</span>}
          </button>
        </div>
      </article>
    </>
  )
}
